import { useState } from "preact/hooks";
import { caption_source, selector,
  anti_selector as settings_anti_selector,
  disable_scroll, magnification, right_click_protection, slideshow,
  separate_galleries, animation_toggle, animation_speed, animation_speeds
} from "./settings";
import useMeowLightboxContext from './context';
import MwlImage from "./components/MwlImage";

// Helpers
/**
 * Reset the mwl-img class to all images matching selector/anti-selector and return the number of images.
 * @returns {number} The number of images found
 */
export const resetMwlImgClass = () => {
  // Remove existing classes from both images and videos.
  document.querySelectorAll("img.mwl-img, video.mwl-video").forEach(($media) => {
    $media.classList.remove("mwl-img", "mwl-video");
  });

  const separatedGalleries = separate_galleries ?? false;

  const scrapper_anti_selector = '.leaflet-tile, .leaflet-google-mutant';
  const anti_selector = settings_anti_selector
    ? [settings_anti_selector, scrapper_anti_selector].join(', ')
    : scrapper_anti_selector;
  const anti_srces = ['maps.googleapis.com', 'api.mapbox.com', 'api.maptiler.com', 'openstreetmap.org'];

  let imgIndex = 0;
  let vidIndex = 0;
  
  // Process each gallery container defined by the variable "selector"
  document.querySelectorAll(selector).forEach((container) => {
    // Process Images:
    container.querySelectorAll("img:not(.mwl-img)").forEach((img) => {
      if (
        !img.closest(anti_selector) &&
        !anti_srces.some((src) => img.src.includes(src))
      ) {
        img.classList.add("mwl-img");
        img.setAttribute("mwl-index", imgIndex);
        imgIndex++;
      }
    });
    // Process Videos:
    container.querySelectorAll("video:not(.mwl-video)").forEach((video) => {
      if (
        !video.closest(anti_selector) &&
        !anti_srces.some((src) => video.src && video.src.includes(src))
      ) {
        video.classList.add("mwl-video");
        video.setAttribute("mwl-index", vidIndex);
        vidIndex++;
      }
    });
  });

  if (separatedGalleries) {
    document.querySelectorAll(selector).forEach((gallery) => {
      let relIndexImg = 0;
      gallery.querySelectorAll('img.mwl-img').forEach((img) => {
        img.setAttribute('mwl-index-relative', relIndexImg);
        relIndexImg++;
      });

      let relIndexVid = 0;
      gallery.querySelectorAll('video.mwl-video').forEach((video) => {
        video.setAttribute('mwl-index-relative', relIndexVid);
        relIndexVid++;
      });
    });
  }

  // Returning total counts may be useful.
  return imgIndex + vidIndex;
};

/**
 * Reads the page to add the mwl-img class to images matching selector/anti-selector
 */
export const scrapThePage = async () => {

  // Wait for the Meow Gallery rendering to finish.
  const mgl_root_selector = '.mgl-root';
  if (document.querySelector(mgl_root_selector)) {
    const mgl_roots = document.querySelectorAll(mgl_root_selector);
    let mgl_roots_rendered = false;
    while (!mgl_roots_rendered) {
      mgl_roots_rendered = Array.from(mgl_roots).every((mgl_root) => mgl_root.querySelector('img'));
      if (!mgl_roots_rendered) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  }

  return resetMwlImgClass();
};

export const retrieveImageId = ($image) => {
  if ($image.getAttribute("data-mwl-img-id")) {
    return $image.getAttribute("data-mwl-img-id");
  }
  const classList = Array.from($image.classList);
  if (classList.length > 0 && classList.some(className => className.includes('wp-image-'))) {
    const className = classList.find(className => className.includes('wp-image-'));
    return className.replace('wp-image-', '');
  }
  return null;
};

export const retrieveVideoId = ($video) => {
  if ($video.getAttribute("data-mwl-video-id")) {
    return $video.getAttribute("data-mwl-video-id");
  }
  const classList = Array.from($video.classList);
  if (classList.length > 0 && classList.some(className => className.includes('wp-video-'))) {
    const className = classList.find(className => className.includes('wp-video-'));
    return className.replace('wp-video-', '');
  }
  return null;
};

/**
 * Create an MwlImage object for each images.
 */
export const retrieveImagesData = () => {
  const $images = document.querySelectorAll(".mwl-img");
  const images = [];

  $images.forEach(($image) => {
    // The image used in the thumbnail
    let current_src = $image.currentSrc;
    if (
      $image.classList.contains("mgl-lazy") &&
      !$image.classList.contains("lazyloaded")
    ) {
      current_src = false;
    }

    const img_id = retrieveImageId($image);

    if (img_id != null && window.mwl_data && window.mwl_data[img_id]?.success) {
      const image_data = window.mwl_data[img_id];

      // Process GPS data.
      if (image_data.data.gps.split(",").length > 1) {
        image_data.gps = {
          lat: parseFloat(image_data.data.gps.split(",")[0]),
          lng: parseFloat(image_data.data.gps.split(",")[1]),
        };
      } else {
        image_data.gps = { lat: "N/A", lng: "N/A" };
      }

      let caption;
      if (caption_source == "description" && image_data.data.description) {
        caption = image_data.data.description;
      }
      if (caption_source == "caption" && image_data.data.caption) {
        caption = image_data.data.caption;
      }

      const data = {
        id: image_data.data.id,
        title: image_data.data.title,
        index: parseInt($image.getAttribute("mwl-index")),
        caption,
        img_low_res_src: current_src,
        img_src: encodeURI(image_data.file),
        img_srcset: image_data.file_srcset,
        img_sizes: image_data.file_sizes,
        dimensions: image_data.dimension,
        img_orientation:
          image_data.dimension.width > image_data.dimension.height
            ? "landscape"
            : "portrait",
        img_exifs: image_data.data,
        img_gps: image_data.gps,
        download_link: image_data.download_link,
      };

      images.push(MwlImage(data));
    } else {
      // Fallback for when no mwl_data exists.
      let img_src = $image.getAttribute("mgl-lazy")
        ? $image.getAttribute("mgl-src")
        : $image.getAttribute("data-lazy-src") || $image.getAttribute("src");

      let img_srcset = $image.getAttribute("data-lazy-srcset") || $image.getAttribute("srcset");
      let img_sizes = $image.getAttribute("data-lazy-sizes") || $image.getAttribute("sizes");

      const data = {
        index: parseInt($image.getAttribute("mwl-index")),
        title: '',
        img_low_res_src: current_src,
        img_src: img_src,
        img_srcset: img_srcset,
        img_sizes: img_sizes,
        caption: false,
        dimensions: false,
        img_orientation: "landscape",
        img_exifs: {
          title: "",
          caption: "",
          camera: "N/A",
          lens: "N/A",
          aperture: "N/A",
          shutter_speed: "N/A",
          iso: "N/A",
          focal_length: "N/A",
          date: "N/A",
          keywords: "N/A",
        },
      };
      images.push(MwlImage(data));
    }
  });

  return images;
};

export const retrieveVideosData = () => {
  const $videos = document.querySelectorAll(".mwl-video");
  const videos = [];

  $videos.forEach(($video) => {
    console.log($video); 

    let current_src = $video.currentSrc;
    if (!current_src) {

      const sourceEl = $video.querySelector("source");
      if (sourceEl) {
        current_src = sourceEl.src;
      }
    }
    if (
      $video.classList.contains("mgl-lazy") &&
      !$video.classList.contains("lazyloaded")
    ) {
      current_src = false;
    }


    const vid_id = retrieveVideoId($video);

    const data = {
      index: parseInt($video.getAttribute("mwl-index")),
      title: $video.getAttribute("title") || "",
      img_low_res_src: current_src,
      img_src: current_src,
      img_srcset: "",
      img_sizes: "",
      caption: $video.getAttribute("data-caption") || false,
      dimensions: {
        width: $video.videoWidth || 0,
        height: $video.videoHeight || 0,
      },
      img_orientation: ($video.videoWidth || 0) > ($video.videoHeight || 0) ? "landscape" : "portrait",
      download_link: $video.getAttribute("data-download") || "",

      img_exifs: {
        title: "",
        caption: "",
        camera: "N/A",
        lens: "N/A",
        aperture: "N/A",
        shutter_speed: "N/A",
        iso: "N/A",
        focal_length: "N/A",
        date: "N/A",
        keywords: "N/A",
      },
    };

    videos.push(MwlImage(data));
  });

  console.log(videos);
  return videos;
};


export const zoomAnimation = ({isOpening, _img, _img_dest, lightbox, rectCreated}) => {
  return new Promise((resolve) => {
    if(rectCreated){
      resolve();
      return;
    }

    const speed = animation_speeds[animation_speed];

    let _img_rect = _img.getBoundingClientRect();
    let _img_dest_rect = _img_dest.getBoundingClientRect();
    const target = isOpening ? _img : _img_dest;

    const _img_clone_container = document.createElement('div');
    const _img_clone = _img.cloneNode(true);
    const _navigations = document.querySelector('.mwl__navigations');

    const layouts = ["carousel", "horizontal", "map"];
    for (const layout of layouts) {
      const layout_rect = target.closest(`.mgl-${layout}-container[layout="${layout}"]`)?.getBoundingClientRect();

      if (layout_rect) {
        switch (layout) {
          case "carousel":
            const carousel_larger_than_img = isOpening ? layout_rect.width > _img_rect.width : layout_rect.width > _img_dest_rect.width;
            let carousel_img_object = isOpening ? _img_rect : _img_dest_rect;
            if (!carousel_larger_than_img) {
            carousel_img_object = {
              width:  layout_rect.width,
              height: isOpening ? _img_rect.height : _img_dest_rect.height,
              top:    layout_rect.top,
              left:   layout_rect.left,
            };
           }
  
            isOpening ? _img_rect = carousel_img_object : _img_dest_rect = carousel_img_object;
            break;

        case "horizontal":
          const horizontal_img_object = {
            width: isOpening ? Math.min(layout_rect.right, _img_rect.right) - Math.max(layout_rect.left, _img_rect.left) : Math.min(layout_rect.right, _img_dest_rect.right) - Math.max(layout_rect.left, _img_dest_rect.left),
            height: isOpening ? _img_rect.height : _img_dest_rect.height,
            top: layout_rect.top,
            left: isOpening ? Math.max(layout_rect.left, _img_rect.left) : Math.max(layout_rect.left, _img_dest_rect.left),
          };
          isOpening ? _img_rect = horizontal_img_object : _img_dest_rect = horizontal_img_object;
          break;

        case "map":
          _img_clone.style.borderRadius = isOpening ? '1000px' : '0px';
          //_img_clone.style.transition = 'border-radius .5s ease-in-out';
          _img_clone.style.transition = `border-radius ${speed}ms ease-in-out`;
          setTimeout(() => {
            _img_clone.style.borderRadius = isOpening ? '0px' : '1000px';
          }, 10);
          break;
        }

        break;
      }
    }

    Object.assign(_img_clone_container.style, {
      position: 'fixed',
      top: `${_img_rect.top}px`,
      left: `${_img_rect.left}px`,
      width: `${_img_rect.width}px`,
      height: `${_img_rect.height}px`,
    });

    const containerTransitionStyles = `transform ${speed}ms ease-in-out, width ${speed}ms ease-in-out, height ${speed}ms ease-in-out, opacity ${speed/2}ms ease-in-out`;
    _img_clone_container.style.transition = containerTransitionStyles;
    _img_clone_container.classList.add('mwl-img-clone-container');
    _img_clone.classList.add('mwl-img-clone');
    _img_clone_container.appendChild(_img_clone);
    document.body.appendChild(_img_clone_container);

    const transitionStyles = `opacity ${speed/2}ms ease-in-out`;
    lightbox.style.transition = transitionStyles;
    if ( _navigations ){ _navigations.style.transition = transitionStyles; } //Might have only one image

    if (isOpening) {
      _img_dest.style.opacity = 0;
      lightbox.style.opacity = 0;
      if ( _navigations ){ _navigations.style.opacity = 0; } //Might have only one image
    } else {
      _img.style.opacity = 0;
      lightbox.style.opacity = 1;
    }

    const translateX = _img_dest_rect.left - _img_rect.left;
    const translateY = _img_dest_rect.top - _img_rect.top;

    setTimeout(() => {
      lightbox.style.opacity = isOpening ? 1 : 0;
      _img_clone_container.style.transform = `translate(${translateX}px, ${translateY}px)`;
      _img_clone_container.style.width = `${_img_dest_rect.width}px`;
      _img_clone_container.style.height = `${_img_dest_rect.height}px`;
    }, 10);

    setTimeout(() => {
      _img_clone_container.style.opacity = isOpening ? 0 : 1;
      isOpening ? (_img_dest.style.opacity = 1, _navigations == undefined ? null : _navigations.style.opacity = 1) : _img.style.opacity = 1;
    }, speed);

    setTimeout(() => {
      
      _img_clone_container.remove();
      resolve();
    }, speed + (0.25*speed));
  });
};
// Hooks
export const useController = () => {

  const { image, images, imagesNumber, currentSeparatedIndexDifference, currentIndex, mwlElement, autoplayInterval } = useMeowLightboxContext();
  const { updateCurrentIndex, updateCurrentUrl, updateIsExtended, updateAutoplayInterval, updateIsPlaying, updateIsMapOpen } = useMeowLightboxContext();


  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [translateXinPercents, setTranslateXinPercents] = useState(undefined);

  const separatedGalleries = separate_galleries ?? false;

  /**
   * Hide UI elements when zooming in for a cleaner view
   */
  const hideUIElements = () => {
    const elements = [
      '.mwl__navigations',
      '.mwl__topbar',
      '.mwl__metadata'
    ];
    
    elements.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        element.style.opacity = '0';
      }
    });
  };

  /**
   * Show UI elements when zooming out
   */
  const showUIElements = () => {
    const elements = [
      '.mwl__navigations',
      '.mwl__topbar',
      '.mwl__metadata'
    ];
    
    elements.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        element.style.opacity = '1';
      }
    });
  };

  /**
   * Unzoom the image
   */
  const unzoom = () => {
    document.querySelectorAll(".mwl img.zoomed").forEach(($image) => {
      $image.classList.remove("zoomed");
      $image.style.transform = "translate3d(0,0,0) scale(1)";
      $image.style.cursor = 'zoom-in';
    });
    setCurrentZoomScale(1);
    setCurrentDistanceMobile({x: 0, y: 0});
    setCurrentDistance({x: 0, y: 0});
    showUIElements();
  };

  /**
   * Hide and destroy map
   */
  const destroyMap = () => {
    const $map = document.querySelector(`#image-map-${currentIndex}`);
    if ($map?.classList.contains("visible")) {
      toggleImageMap();
    }
    const $map_parent = $map?.parentNode;
    if ($map_parent) {
      $map_parent.removeChild($map);
      const $newMap = document.createElement("div");
      $newMap.setAttribute("id", `image-map-${currentIndex}`);
      $newMap.setAttribute("class", "image-map");
      $map_parent.appendChild($newMap);
    }
  };

  /**
   * Stops the slideshow
   */
  const stopInterval = () => {
    const $playButton = document.querySelector(
      ".mwl__topbar__controls__control--play"
    );
    const $pauseButton = document.querySelector(
      ".mwl__topbar__controls__control--pause"
    );
    if ($playButton && $pauseButton) {
      $pauseButton.classList.add("hidden");
      $playButton.classList.remove("hidden");
      clearInterval(autoplayInterval);
    }
  };

  /**
   * Hides images metadatas to only leave the image itself.
   */
  const extendLightbox = () => {
    mwlElement.classList.add('extended');
    document.querySelector('.visible > .mwl__slider__image__thumbnail').classList.add('mwl__full__screen');
    updateIsExtended(true);
    document.querySelector('.mwl__topbar__controls__control--fullscreen').classList.add('hidden');
    document.querySelector('.mwl__topbar__controls__control--shrink').classList.remove('hidden');
  };

  /**
   * Shows images metadatas.
   */
  const shrinkLightbox = () => {
    mwlElement.classList.remove('extended');
    document.querySelector('.visible > .mwl__slider__image__thumbnail').classList.remove('mwl__full__screen');
    updateIsExtended(false);
    document.querySelector('.mwl__topbar__controls__control--fullscreen').classList.remove('hidden');
    document.querySelector('.mwl__topbar__controls__control--shrink').classList.add('hidden');
  };

  /**
   * Starts the slideshow
   */
  const startInterval = () => {
    const $playButton = document.querySelector('.mwl__topbar__controls__control--play');
    const $pauseButton = document.querySelector('.mwl__topbar__controls__control--pause');
    if ($playButton && $pauseButton) {
      $playButton.classList.add('hidden');
      $pauseButton.classList.remove('hidden');
      let baseIndex = currentIndex;
      const newAutoplayInterval = setInterval(() => {
        baseIndex = goToNext(baseIndex);
      }, slideshow.slide_duration);
      updateAutoplayInterval(newAutoplayInterval);
    }
  };

  /**
   * Toggles the map
   */
  const toggleImageMap = () => {
    // Unzoom
    unzoom();


    const $map = document.querySelector(`#image-map-${currentIndex}`);
    // Add an onclick listener to the map to prevent the lightbox from closing when clicking on the map
    if ($map) {
      $map.onclick = onClickOnMap;
    }

    const map_id = $map.getAttribute('id');
    const $image_container = document.querySelector(`.mwl__slider__image[data-index="${currentIndex}"]`);
    const $image = $image_container.querySelector('img.mwl__slider__image__thumbnail__high-res');
    const image = images[currentIndex];
    if (!$map.classList.contains('visible')) {
      updateIsMapOpen(true);

      $map.classList.add('visible');
      $map.style.width = `${$image.offsetWidth}px`;
      $map.style.height = `${$image.offsetHeight}px`;
      // check if window.mwlInitMap is defined
      if (typeof window.mwlInitMap === 'undefined') {
        console.error('window.mwlInitMap is not defined. Please check that you have included the map script in your page.');
        return;
      }
      window.mwlInitMap(map_id, image);
    } else {
      updateIsMapOpen(false);
      $map.classList.remove('visible');
      destroyMap();
      $map.style.width = 0;
      $map.style.height = 0;
    }
  };

  /**
  * Downloads the current image
  */
  const downloadImage = () => {
    const href = images[currentIndex].download_link;
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = href.split(/[\\/]/).pop();
    document.body.appendChild(anchor);
    anchor.click();
  };




  /**
   * Closes the lightbox
   */
  const closeLightbox = () => {


    const closeLogic = () => {

    
      // The rest of the code will be executed after the animation is finished
      if (disable_scroll) {
        document.querySelector("body").classList.remove("no-scroll");
      }
      const newIndex = false;

      // Unzoom
      unzoom();

      // Hide map
      destroyMap();

      // Stop slideshow
      stopInterval();
      updateIsPlaying(false);

      updateIsMapOpen(false);

      updateCurrentIndex(newIndex);

      mwlElement.classList.remove("visible");

      updateUrl(newIndex);
    };

    const hasZoomAnimation = animation_toggle === 'zoom_in_out';
    if ( hasZoomAnimation ) {
      const _img_dest = document.querySelector(`.mwl-img[mwl-index="${currentIndex}"]`);
      let _img = document.querySelector(`.mwl__slider__image[data-index="${currentIndex}"] > .mwl__slider__image__thumbnail > img.landscape`);
      _img = _img == undefined ? document.querySelector(`.mwl__slider__image[data-index="${currentIndex}"] > .mwl__slider__image__thumbnail > img`) : _img;

      unZoomImage(_img);

      requestAnimationFrame(() => {
        zoomAnimation({isOpening: false, _img, _img_dest, lightbox: mwlElement, rectCreated: false}).then(() => {
          closeLogic();
        });
      });
    }else{
      closeLogic();
    }

  };

  /**
   * Slide to a specific index
   */
  const slideTo = (index) => {
    // Unzoom
    unzoom();

    // Hide map
    destroyMap();

    updateCurrentIndex(index);

    // Reset zoom state when changing images
    setCurrentZoomScale(1);
    setCurrentDistanceMobile({x: 0, y: 0});
    setCurrentDistance({x: 0, y: 0});
  };

  /**
   * Slides to the previous image
   */
  const goToPrev = () => {
    let newIndex = currentIndex - 1;

    if ( separatedGalleries ) {
      const separatedGalleryIndex = (currentIndex - currentSeparatedIndexDifference);
      if ( separatedGalleryIndex == 0 ) {
        newIndex = imagesNumber + currentSeparatedIndexDifference - 1;
      }

    }else{
      if (currentIndex == 0) { newIndex = images.length - 1; }
    }

    
    slideTo(newIndex);
  };

  /**
   * Slides to the next image
   */
  const goToNext = (customCurrentIndex) => {
    const baseIndex = customCurrentIndex == null ? currentIndex : customCurrentIndex;
    let newIndex = baseIndex + 1;
    
    if( separatedGalleries ) {
      const separatedGalleryIndex = (baseIndex - currentSeparatedIndexDifference + 1);

      if ( separatedGalleryIndex >= imagesNumber ){
        newIndex = currentSeparatedIndexDifference;
      }
    }else{
      if (baseIndex >= images.length - 1) { newIndex = 0; }
    }

    slideTo(newIndex);
    return newIndex;
  };



  /**
   * Right-click protection
   */
  const rightClickProtection = (e) => {
    if (right_click_protection) {
      e.preventDefault();
    }
  };

  /**
   * Image zoom
   */
  const [isPan, setIsPan] = useState(false);

  const [mouseDownStartPos, setMouseDownStartPos] = useState({x: 0, y: 0});
  const [absoluteDownStartPos, setAbsoluteDownStartPos] = useState({x: 0, y: 0});

  const [currentDistance, setCurrentDistance] = useState({x: 0, y: 0});

  let original_transition = '';

  const onImageMouseDown = (e) => {
    e.preventDefault();
    const $image = e.target;
    const is_zoomed = $image.classList.contains('zoomed');
    if (is_zoomed) {

      setAbsoluteDownStartPos({ x: e.clientX, y: e.clientY });

      setMouseDownStartPos({
        x: e.clientX - currentDistance.x,
        y: e.clientY - currentDistance.y,
      });
      
      setIsPan(true);
  
      original_transition = $image.style.transition;
      $image.style.transition = 'none';
      $image.style.cursor = 'grabbing';
    }
  }
  
  const onImageMouseUp = (e) => {
    e.preventDefault();
    const $image = e.target;
    const is_zoomed = $image.classList.contains('zoomed');
    if (is_zoomed) {
      setIsPan(false);
      $image.style.transition = original_transition;
      $image.style.cursor = 'grab';
    }
  }
  
  const onImageMouseMove = (e) => {
    e.preventDefault();

    const $image = e.target;
    const is_zoomed = $image.classList.contains('zoomed');
  
    if (is_zoomed && isPan) {
      const mouseDownStartPosX = mouseDownStartPos.x;
      const mouseDownStartPosY = mouseDownStartPos.y;
  
      const mouseMovePosX = e.clientX;
      const mouseMovePosY = e.clientY;
  
      const dx = mouseMovePosX - mouseDownStartPosX;
      const dy = mouseMovePosY - mouseDownStartPosY;
      
      $image.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${currentZoomScale})`;
      // Synchronize both mobile and desktop state
      setCurrentDistance({x: dx, y: dy});
      setCurrentDistanceMobile({x: dx, y: dy});
    }
  }

  const unZoomImage = (e) => {
    const $image = e.target ? e.target : e;

    if ($image.classList.contains('zoomed') ) {
      $image.classList.remove('zoomed');
      $image.style.transform = 'translate3d(0,0,0) scale(1)';
      $image.style.cursor = 'zoom-in';
      setCurrentZoomScale(1);
      setCurrentDistanceMobile({x: 0, y: 0});
      setCurrentDistance({x: 0, y: 0});
      showUIElements();
    }
  };



  const zoomImage = (e) => {
    const $image = e.target;
    if (magnification.enabled && $image && !$image.classList.contains('swiping')) {
      if (!$image.classList.contains('zoomed')) {
        $image.classList.add('zoomed');

        // const width = $image.naturalWidth;
        // const height = $image.naturalHeight;

        // $image.style.width = `${width}px`;
        // $image.style.height = `${height}px`;

        const imageBounds = $image.getBoundingClientRect();
        const imageLeft = imageBounds.left;
        const imageTop = imageBounds.top;
        const XrelativeToImageCenter = e.clientX - (imageLeft + imageBounds.width / 2);
        const YrelativeToImageCenter = e.clientY - (imageTop + imageBounds.height / 2);
        const translateX = XrelativeToImageCenter - XrelativeToImageCenter * 2;
        const translateY = YrelativeToImageCenter - YrelativeToImageCenter * 2;
  
        $image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(2)`;
        $image.style.cursor = 'grab';

        // Synchronize both mobile and desktop state AND set zoom scale to 2
        setCurrentDistanceMobile({ x: translateX, y: translateY });
        setCurrentDistance({ x: translateX, y: translateY });
        setCurrentZoomScale(2);
        hideUIElements();
      } else {
        if (e.clientX === absoluteDownStartPos.x && e.clientY === absoluteDownStartPos.y) {
          unZoomImage(e);
        }
      }
    }
  };

  const preventClickEvent = (e) => {
    e.stopPropagation();
  };

    /**
   * Slider swipe
   */

    const [countDebounce, setCountDebounce] = useState(0);
    const [touchStartPos, setTouchStartPos] = useState({x: 0, y: 0});
    const [currentDistanceMobile, setCurrentDistanceMobile] = useState({x: 0, y: 0});
    const [currentZoomScale, setCurrentZoomScale] = useState(1);
    const [initialPinchDistance, setInitialPinchDistance] = useState(0);
    const [isPinchGesture, setIsPinchGesture] = useState(false);
    const [pinchEndTimeout, setPinchEndTimeout] = useState(null);
    const [pinchStartTime, setPinchStartTime] = useState(0);

    const getSliderRoot = (ele) => {
      if (ele.classList.contains('mwl__slider')) {
        return ele;
      }
      return getSliderRoot(ele.parentNode);
    };
  
  
    let zoomDistance = 0;
  
    const sliderMoveStart = (e) => {
      setCountDebounce(0);
      const $image = e.target;
  
      // Clear any existing pinch end timeout
      if (pinchEndTimeout) {
        clearTimeout(pinchEndTimeout);
        setPinchEndTimeout(null);
      }
  
      // Handle pinch zoom start
      if (e.touches.length === 2) {
        const finger_one = e.touches[0];
        const finger_two = e.touches[1];
        const distance = Math.sqrt(Math.pow(finger_one.clientX - finger_two.clientX, 2) + Math.pow(finger_one.clientY - finger_two.clientY, 2));
        
        setInitialPinchDistance(distance);
        setIsPinchGesture(true);
        setPinchStartTime(Date.now());
        
        // If image is already zoomed from tap-to-zoom, extract current state
        if ($image.classList.contains('zoomed')) {
          const style = $image.style.transform;
          const scaleMatch = style.match(/scale\(([^)]+)\)/);
          const translateMatch = style.match(/translate3d\(([^,]+)px,\s*([^,]+)px/);
          
          if (scaleMatch) {
            const currentScale = parseFloat(scaleMatch[1]);
            setCurrentZoomScale(currentScale);
          }
          if (translateMatch) {
            const currentTranslateX = parseFloat(translateMatch[1]);
            const currentTranslateY = parseFloat(translateMatch[2]);
            setCurrentDistanceMobile({ x: currentTranslateX, y: currentTranslateY });
            setCurrentDistance({ x: currentTranslateX, y: currentTranslateY });
          }
        }
        
        return;
      }

      // If we're transitioning from pinch to single touch, ignore this touch start
      if (isPinchGesture) {
        return;
      }

      // If image is zoomed, we're in pan mode, not swipe mode
      if ($image.classList.contains('zoomed')) {
        setTouchStartPos({
          x: e.touches[0].clientX - currentDistanceMobile.x,
          y: e.touches[0].clientY - currentDistanceMobile.y,
        });
        return;
      }
  
      if (images.length < 2) {
        return;
      }
  
      // Regular swipe logic for non-zoomed images
      setTouchStartPos({
        x: e.touches[0].clientX - currentDistanceMobile.x,
        y: e.touches[0].clientY - currentDistanceMobile.y,
      });
      

      const clientX = e.touches[0].clientX;
      setTouchStartX(clientX);
      setTouchEndX(clientX);
      setTranslateXinPercents(getSliderRoot(e.target).style.transform.match(/(\-?[0-9]*%)/)[0] ?? 0);
    };
  
    
    const sliderMoving = (e) => {
      e.preventDefault();
      const $image = e.target;
  
      // Handle pinch zoom
      if(e.touches.length == 2) {
        const finger_one = e.touches[0];
        const finger_two = e.touches[1];
  
        const currentDistance = Math.sqrt(Math.pow(finger_one.clientX - finger_two.clientX, 2) + Math.pow(finger_one.clientY - finger_two.clientY, 2));
        
        if (initialPinchDistance > 0) {
          // Calculate new scale based on the change in distance relative to the current zoom level
          const scaleChange = currentDistance / initialPinchDistance;
          const newScale = currentZoomScale * scaleChange;
          
          // Constrain zoom between 1x and 4x
          const constrainedScale = Math.max(1, Math.min(4, newScale));
          
          if (constrainedScale <= 1) {
            $image.style.transform = `translate3d(0px, 0px, 0) scale(1)`;
            $image.classList.remove('zoomed');
            $image.style.cursor = 'zoom-in';
            // Reset pan position when fully unzoomed
            setCurrentDistanceMobile({x: 0, y: 0});
            setCurrentDistance({x: 0, y: 0});
            showUIElements();
          } else {
            // Maintain the current pan position while scaling
            $image.style.transform = `translate3d(${currentDistanceMobile.x}px, ${currentDistanceMobile.y}px, 0) scale(${constrainedScale})`;
            $image.classList.add('zoomed');
            $image.style.cursor = 'grab';
            hideUIElements();
          }
        }
        return;
      }

      // If we're transitioning from pinch to single touch, ignore this touch start
      if (isPinchGesture && e.touches.length === 1) {
        return;
      }
  
      if ( $image.classList.contains('zoomed') ) {
        original_transition = $image.style.transition;
        $image.style.transition = 'none'; 

        const touchStartPosX = touchStartPos.x;
        const touchStartPosY = touchStartPos.y;
  
        const touchMovePosX = e.touches[0].clientX;
        const touchMovePosY = e.touches[0].clientY;
  
        const dx = touchMovePosX - touchStartPosX;
        const dy = touchMovePosY - touchStartPosY;
        
        // Ensure we maintain the correct zoom scale - use currentZoomScale if > 1, otherwise 2 for tap-zoom
        const scaleToUse = currentZoomScale > 1 ? currentZoomScale : 2;
        
        // Keep the image zoomed while panning
        $image.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${scaleToUse})`;
        // Synchronize both mobile and desktop state
        setCurrentDistanceMobile( {x: dx, y: dy} );
        setCurrentDistance( {x: dx, y: dy} );
        
        // Update zoom scale if it wasn't set correctly
        if (currentZoomScale <= 1) {
          setCurrentZoomScale(scaleToUse);
        }

        return;
      }
      
      // Regular slider swipe logic only if image is not zoomed
      if (images.length < 2) {
        return;
      }

      const instantTouchX = e.touches[0].clientX;
      const instantDeltaX = instantTouchX - touchStartX;
      setTouchEndX(instantTouchX);
  
      setCountDebounce(countDebounce + 1);
      
      if (Math.abs(instantDeltaX) > 2) {
        const $slider = getSliderRoot(e.target);
        $slider.classList.remove("mwl-animate");
        $slider.style.transition = 'none';
        $slider.style.transform = `translate3d(calc(${translateXinPercents} + ${instantDeltaX}px), 0px, 0px)`;
      }
    };
  
    const sliderMoveEnd = (e) => {
      const $image = e.target;
      
      // Handle end of pinch gesture
      if (e.changedTouches.length > 0 && initialPinchDistance > 0) {
        // Extract current scale from transform and update state
        const transform = $image.style.transform;
        const scaleMatch = transform.match(/scale\(([^)]+)\)/);
        if (scaleMatch) {
          let scale = parseFloat(scaleMatch[1]);
          
          // Calculate pinch speed and final distance for smart unzoom detection
          const pinchDuration = Date.now() - pinchStartTime;
          const finalDistance = Math.sqrt(
            Math.pow(e.changedTouches[0].clientX - (e.changedTouches[1]?.clientX || e.changedTouches[0].clientX), 2) + 
            Math.pow(e.changedTouches[0].clientY - (e.changedTouches[1]?.clientY || e.changedTouches[0].clientY), 2)
          );
          
          // Smart unzoom conditions:
          // 1. Very fast pinch (less than 300ms) with scale reduction
          // 2. Fingers are very close together (less than 50px)
          // 3. Scale is already close to 1 (less than 1.3x)
          const isFastPinch = pinchDuration < 300 && scale < currentZoomScale;
          const fingersVeryClose = finalDistance < 50;
          const closeToOriginalSize = scale < 1.3;
          
          if ((isFastPinch || fingersVeryClose || closeToOriginalSize) && scale < 1.8) {
            // Snap to fully unzoomed and reset pan position
            scale = 1;
            $image.style.transform = `translate3d(0px, 0px, 0) scale(1)`;
            setCurrentDistanceMobile({x: 0, y: 0});
            setCurrentDistance({x: 0, y: 0});
            showUIElements();
          }
          
          setCurrentZoomScale(scale);
          
          // Reset zoom state if scale is 1
          if (scale <= 1) {
            setCurrentZoomScale(1);
            setCurrentDistanceMobile({x: 0, y: 0});
            setCurrentDistance({x: 0, y: 0});
            $image.classList.remove('zoomed');
            $image.style.cursor = 'zoom-in';
            showUIElements();
          } else {
            $image.style.cursor = 'grab';
            hideUIElements();
          }
        }
        setInitialPinchDistance(0);
        setPinchStartTime(0);
        
        // Set a timeout to allow pinch gesture to fully end before allowing single touch
        const timeout = setTimeout(() => {
          setIsPinchGesture(false);
          setPinchEndTimeout(null);
        }, 150); // 150ms delay
        setPinchEndTimeout(timeout);
        
        return;
      }

      // If we're still in pinch gesture transition, don't process as touch end
      if (isPinchGesture) {
        return;
      }

      // If image is zoomed, we were just panning, restore transition and don't trigger slide
      if ($image.classList.contains('zoomed')) {
        $image.style.transition = original_transition;
        return;
      }
      
      if (images.length < 2) {
        return;
      }
      const endDeltaX = touchEndX - touchStartX;
      const threshold = 200;
  
      const $slider = getSliderRoot(e.target);
      $slider.classList.add("mwl-animate");
  
      const quickSwipe = (countDebounce > 2 && countDebounce < 20);
      if (Math.abs(endDeltaX) < threshold && !quickSwipe ) {
  
        $slider.style.transition = 'transform 0s ease';
        $slider.style.transform = `translate3d(${translateXinPercents}, 0px, 0px)`;
       
      } else {
        if (endDeltaX > 0) {
          // positive
          goToPrev();
        } else {
          // negative
          goToNext();
        }
      }
    };

  const updateUrl = (index) => updateCurrentUrl(index);
  const onCloseClick = () => closeLightbox();
  const onFullscreenClick = () => extendLightbox();
  const onShrinkClick = () => shrinkLightbox();
  const onPlayClick = () => { startInterval(); updateIsPlaying(true); };
  const onPauseClick = () => { stopInterval(); updateIsPlaying(false); };
  const onMapClick = () => toggleImageMap();
  const onDownloadClick = () => downloadImage();
  const onPrevClick = () => goToPrev();
  const onNextClick = () => goToNext();
  const onImageClick = (e) => { e.stopPropagation(); zoomImage(e); };
  const onTouchStart = (e) => { sliderMoveStart(e); };
  const onTouchMove = (e) => { sliderMoving(e); };
  const onTouchEnd = (e) => { sliderMoveEnd(e); };
  const onContextMenu = (e) => { rightClickProtection(e); };
  const onKeyDown = (e) => {
    if (!image) return;

    switch (e.key) {
    case "Escape":
      onCloseClick();
      break;

    case "ArrowLeft":
      onPrevClick();
      break;

    case "ArrowRight":
      onNextClick();
      break;

    default:
      return;
    }
  };
  const onClickOnMap = (e) => { preventClickEvent(e); };

  return {
    onCloseClick,
    onFullscreenClick,
    onShrinkClick,
    onPlayClick,
    onPauseClick,
    onMapClick,
    onDownloadClick,
    onPrevClick,
    onNextClick,
    onImageClick,
    onImageMouseDown, 
    onImageMouseMove,
    onImageMouseUp,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onContextMenu,
    onKeyDown,
    onClickOnMap,
  };
};

